import { EPUB_ANNOTATION_PRESENTATION } from 'constants';
import { DEFAULT_COLOR } from 'constants/appStyling/options';
import UserContentItemWithLocation from '../UserContentItemWithLocation';

class UserContentHighlight extends UserContentItemWithLocation {
  static typeName = 'highlights';

  color;
  originalColor;
  snippet;
  presentation;
  noteTextFromV6;

  isReadOnly = false;
  avatar = 'H';

  constructor(properties = {}) {
    super(...arguments);

    this.color =
      this.isSaved && this.isPersisted
        ? (properties.color ?? DEFAULT_COLOR)
        : properties.color;
    this.originalColor = this.color; // for preserving old V6 highlight/note combination models

    this.snippet = properties.snippet;
    this.presentation =
      properties.presentation || EPUB_ANNOTATION_PRESENTATION.HIGHLIGHT;

    this.noteTextFromV6 =
      properties.noteTextFromV6 ||
      properties.presentation ===
        EPUB_ANNOTATION_PRESENTATION.HIGHLIGHT_WITH_NOTE
        ? properties.userText
        : null;
  }

  serialize() {
    const { color, snippet, presentation, noteTextFromV6 } = this;

    return {
      ...super.serialize(),

      color,
      snippet,
      presentation,
      noteTextFromV6,
    };
  }

  get truncatedSnippet() {
    return this.constructor.getTruncatedSnippet(this.snippet);
  }

  async persist({ createMutation, updateMutation, deleteMutation }) {
    const { color, snippet } = this;

    if (this.isPersistedOnce) {
      const isV6HighlightWithNote =
        this.presentation ===
          EPUB_ANNOTATION_PRESENTATION.HIGHLIGHT_WITH_NOTE &&
        this.noteTextFromV6;

      if (isV6HighlightWithNote) {
        // The existing model is a v6 highlight/note hybrid
        // Create an additional record for the note, even if deleting the highlight.
        await createMutation({
          variables: {
            input: {
              color: this.originalColor, // preserve original record color
              snippet,
              presentation: EPUB_ANNOTATION_PRESENTATION.NOTE,
              comment: this.noteTextFromV6,
              ...this.locationPropertiesForGraphQL,
            },
          },
        });
      }

      if (this.shouldDelete) {
        await deleteMutation({ variables: { id: this.pk } });

        return null;
      }

      const {
        data: { updateEpubAnnotation },
      } = await updateMutation({
        variables: {
          input: {
            color,
            presentation: EPUB_ANNOTATION_PRESENTATION.HIGHLIGHT, // if it's a v6 model, update it to a v7 model
            comment: '', // clear any associated note text when updating from a v6 to v7 model
          },
          id: this.pk,
        },
      });

      if (!updateEpubAnnotation.epubAnnotation) {
        console.warn(
          'Failed to update an EpubAnnotation record through GraphQL. It may not exist in the database.',
        );

        return null;
      }

      return updateEpubAnnotation.epubAnnotation.id;
    }

    // Save a new highlight
    const {
      data: { createEpubAnnotation },
    } = await createMutation({
      variables: {
        input: {
          color,
          snippet,
          presentation: EPUB_ANNOTATION_PRESENTATION.HIGHLIGHT,
          ...this.locationPropertiesForGraphQL,
        },
      },
    });

    return createEpubAnnotation.epubAnnotation.id;
  }
}

export default UserContentHighlight;
